import React from "react";

import classnames from "classnames";
import PropTypes from "prop-types";
import { withStyleProps, type Size } from "Utils/styleProps";

type HeaderColor = "error" | "warning" | "success" | "muted" | "white";

interface HeaderProps {
  inline?: boolean;
  level?: number;
  classes?: string;
  color?: HeaderColor;
  className?: string;
  children?: React.ReactNode;
}

const sizeClass = (level: number): string => {
  const map: Record<number, string> = {
    1: "text-6xl",
    2: "text-5xl",
    3: "text-4xl",
    4: "text-3xl",
    5: "text-2xl",
    6: "text-xl",
    7: "text-lg",
  };
  return map[level] ?? "text-base";
};

const weightClass = (level: number): string => {
  if (level <= 2) return "font-light";
  if (level <= 5) return "font-normal";
  return "font-semibold";
};

const colorClass: Record<HeaderColor, string> = {
  error: "text-red-600",
  warning: "text-yellow-600",
  success: "text-green-600",
  muted: "text-gray-500",
  white: "text-white",
};

const HeaderBase = React.forwardRef<HTMLHeadingElement, HeaderProps & React.HTMLAttributes<HTMLHeadingElement>>(
  ({ inline, level = 1, classes, color, className: additionalClassName, children, ...rest }, ref) => {
    const clampedLevel = Math.min(level, 6);
    const className = classnames(sizeClass(level), weightClass(level), color ? colorClass[color] : "text-gray-900", { inline }, classes, additionalClassName);
    return React.createElement(
      `h${clampedLevel}`,
      {
        className,
        ref,
        ...rest,
      },
      [children],
    );
  },
);

HeaderBase.displayName = "Header";

(HeaderBase as any).propTypes = {
  inline: PropTypes.bool,
  level: PropTypes.number,
  color: PropTypes.oneOf(["error", "warning", "success", "muted", "white"]),
  className: PropTypes.string,
  children: PropTypes.node,
};

// Old Naming
export const H1 = withStyleProps(HeaderBase, { level: 1 });
export const H2 = withStyleProps(HeaderBase, { level: 2 });
export const H3 = withStyleProps(HeaderBase, { level: 3 });
export const H4 = withStyleProps(HeaderBase, { level: 4 });
export const H5 = withStyleProps(HeaderBase, { level: 5 });
export const Title = withStyleProps(HeaderBase, { level: 6 });
export const Subtitle = withStyleProps(HeaderBase, { level: 7 });

// New Naming
export const TitleLarge = withStyleProps(HeaderBase, { level: 3 });
export const TitleMedium = withStyleProps(HeaderBase, { level: 5 });
export const TitleSmall = withStyleProps(HeaderBase, { level: 7 });

const Header = withStyleProps(HeaderBase);
export default Header;
